import { getCookie, removeCookie, setCookie } from 'react-use-cookie'

import { getApexDomain } from 'src/lib/js/cookieUtils'
import { Channel } from 'public/js/siteUtilities'

export type CartStorageConfig = {
  isCustomDomain: boolean
  cookieStorageEnabled: boolean
}

export interface StorageEngine {
  get(key: string): string | null
  set(key: string, value: string): void
  clear(key: string): void
}

class LocalStorageEngine implements StorageEngine {
  get(key: string): string | null {
    return localStorage.getItem(key)
  }

  set(key: string, value: string): void {
    localStorage.setItem(key, value)
  }

  clear(key: string): void {
    localStorage.setItem(key, '')
  }
}

class CookieStorageEngine implements StorageEngine {
  private static readonly COOKIE_EXPIRY_DAYS = 7

  get(key: string): string | null {
    const value = getCookie(key)
    return value || null
  }

  set(key: string, value: string): void {
    this._set(key, value)
  }

  private _set(
    key: string,
    value: string,
    expiresDays: number = CookieStorageEngine.COOKIE_EXPIRY_DAYS
  ): void {
    const domain = getApexDomain(window.location.hostname)
    setCookie(key, value, {
      days: expiresDays,
      SameSite: 'Lax',
      domain: domain
    })
  }

  clear(key: string): void {
    removeCookie(key)
  }
}

/**
 * Storage engine that delegates to both cookie and localStorage, reading from
 * both and preferring the cookie value when present. Writes and clears are
 * applied to both backends. If a key exists in only one backend (e.g. after a
 * feature flag or config change), get() will sync the value to the other so
 * both stay consistent.
 */
class HybridStorageEngine implements StorageEngine {
  private readonly storageEngines: StorageEngine[]

  constructor() {
    this.storageEngines = [new CookieStorageEngine(), new LocalStorageEngine()]
  }

  get(key: string): string | null {
    const values = this.storageEngines.map((engine) => engine.get(key))
    const foundValues = values.filter((val) => val !== null)
    const foundValue = foundValues[0] // prefence the cookie storage engine (idx: 0) if both engines have a value.

    if (values.length > foundValues.length && foundValue) {
      /* If only one of the storage engines has a value stored, spread it to both storage engines.
       * This ensures we can maintain consistency across feature flag configurations.
       */
      this.set(key, foundValue)
    }
    return foundValue ?? null
  }

  set(key: string, value: string): void {
    this.storageEngines.forEach((engine) => engine.set(key, value))
  }

  clear(key: string): void {
    this.storageEngines.forEach((engine) => engine.clear(key))
  }
}

class CartStorage {
  private readonly isCustomDomain: boolean
  private readonly cookieStorageEnabled: boolean
  private readonly guidStorageEngine: StorageEngine

  constructor(config: CartStorageConfig) {
    this.isCustomDomain = config.isCustomDomain
    this.cookieStorageEnabled = config.cookieStorageEnabled
    this.guidStorageEngine = this.createStorageEngine()
  }

  private createStorageEngine(): StorageEngine {
    if (this.shouldUseCookies()) {
      return new HybridStorageEngine()
    }
    return new LocalStorageEngine()
  }

  private isCookieAvailable(): boolean {
    return typeof document !== 'undefined'
  }

  private shouldUseCookies(): boolean {
    return (
      this.isCustomDomain && // We must only use cookies on custom domains to prevent carts being shared across *.toast.site domains.
      this.cookieStorageEnabled &&
      this.isCookieAvailable()
    )
  }

  private getCartKey(channel: Channel, locationId: string): string {
    return `toast-embedded-${channel}-cart|${locationId}`
  }

  getCartGuid(channel: Channel, locationId: string): string | null {
    const cartKey = this.getCartKey(channel, locationId)
    return this.guidStorageEngine.get(cartKey)
  }

  setCartGuid(channel: Channel, locationId: string, cartGuid: string): void {
    const cartKey = this.getCartKey(channel, locationId)
    this.guidStorageEngine.set(cartKey, cartGuid)
  }

  clearCartGuid(channel: Channel, locationId: string): void {
    const cartKey = this.getCartKey(channel, locationId)
    this.guidStorageEngine.clear(cartKey)
  }

  getIsCustomDomain(): boolean {
    return this.isCustomDomain
  }

  getCookieStorageEnabled(): boolean {
    return this.cookieStorageEnabled
  }
}

export default CartStorage
